import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate } from 'react-router-dom';

import { NAV, settingsSectionsFor } from '../nav';
import { SettingsMenu } from '../SettingsMenu';
import { SprintBlock } from '../SprintBlock';
import { UpgradeBlock } from '../UpgradeBlock';
import { useLayout } from '@/app/store/layout.store';
import { useSession } from '@/app/store/session.store';
import { authApi, endSession } from '@/features/auth';
import { useUnreadCount } from '@/features/messaging';
import { useMemberCount } from '@/features/tenant';
import { Avatar } from '@/shared/ui/atoms/Avatar';
import { Icon } from '@/shared/ui/atoms/Icon';
import './Sidebar.styl';

const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');

export function Sidebar() {
  const user = useSession((s) => s.user);
  const { t } = useTranslation();
  const showUpgrade = useLayout((s) => s.showUpgrade);
  const unread = useUnreadCount();
  const people = useMemberCount();
  const navigate = useNavigate();
  const logout = useMutation({
    mutationFn: authApi.logout,
    onSettled: () => {
      endSession();
      navigate('/login', { replace: true });
    }
  });

  return (
    <div className="w-sidebar">
      <div className="w-sidebar__workspace">
        <div className="w-sidebar__tenant">
          <Avatar
            initials={initialsOf(user?.tenantName ?? 'Syncra')}
            color="#e0f7f9"
            src={user?.tenantLogoUrl ?? null}
            size={36}
            square
            className="w-sidebar__tenant-mark"
          />
          <div className="w-sidebar__tenant-text">
            <div className="w-sidebar__tenant-name">{user?.tenantName}</div>
            <div className="w-sidebar__tenant-meta">
              {people !== null
                ? t('shell.workspacePeople', { count: people })
                : t('shell.workspace')}
            </div>
          </div>
          <span className="w-sidebar__tenant-switch" aria-hidden="true">
            <Icon name="chevronDown" size={11} strokeWidth={2} />
          </span>
        </div>
      </div>

      <div className="w-sidebar__scroll">
        <section className="w-sidebar__section w-sidebar__section--menu">
          <div className="w-sidebar__label">{t('shell.menu')}</div>
          <nav className="w-sidebar__nav" aria-label={t('nav.main')}>
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) =>
                  isActive
                    ? 'w-sidebar__item w-sidebar__item--active'
                    : 'w-sidebar__item'
                }
              >
                <Icon name={n.icon} />
                <span className="w-sidebar__item-label">
                  {t(`nav.${n.key}`)}
                </span>
                {n.to === '/messages' && unread > 0 ? (
                  <span className="w-sidebar__badge">{unread}</span>
                ) : null}
              </NavLink>
            ))}
          </nav>
        </section>

        <section className="w-sidebar__section w-sidebar__section--sprint">
          <div className="w-sidebar__label">{t('shell.activeSprint')}</div>
          <SprintBlock />
        </section>

        <section className="w-sidebar__section w-sidebar__section--general">
          <div className="w-sidebar__label">{t('shell.general')}</div>
          <nav className="w-sidebar__nav" aria-label={t('nav.general')}>
            <SettingsMenu
              to="/settings"
              label={t('shell.settings')}
              sections={settingsSectionsFor(user)}
            />
            <a className="w-sidebar__item" href="mailto:support@syncra.app">
              <Icon name="help" />
              <span className="w-sidebar__item-label">{t('shell.help')}</span>
            </a>
            <button
              type="button"
              className="w-sidebar__item"
              onClick={() => logout.mutate()}
              disabled={logout.isPending}
            >
              <Icon name="logout" />
              <span className="w-sidebar__item-label">{t('shell.logout')}</span>
            </button>
          </nav>
        </section>
      </div>

      {showUpgrade ? <UpgradeBlock /> : null}
    </div>
  );
}
